e:<template>
  <div class="material-center">
    <div class="banner-tip">
      素材中心管理的是本地可重复使用的素材文件夹：请指定混剪根目录（例如：D:\混剪视频），页面会优先显示上次扫描结果。
    </div>

    <div class="toolbar">
      <div class="toolbar-left">
        <span class="toolbar-label">混剪根目录</span>
        <span class="toolbar-path">{{ rootPath || '未选择' }}</span>
      </div>
      <div class="toolbar-right">
        <input
          ref="folderInputRef"
          type="file"
          webkitdirectory
          directory
          multiple
          style="display:none"
          @change="onFolderSelected"
        />
        <a-button type="primary" class="btn-select-folder" :loading="selecting" @click="triggerFolderSelect">
          <folder-open-outlined /> 选择文件夹
        </a-button>
      </div>
    </div>

    <div class="stat-cards">
      <div class="stat-card">
        <div class="stat-value stat-value-blue">{{ folderCount }}</div>
        <div class="stat-label">已接入文件夹</div>
      </div>
      <div class="stat-card">
        <div class="stat-value stat-value-green">{{ totalClips }}</div>
        <div class="stat-label">可用片段总数</div>
      </div>
      <div class="stat-card">
        <div class="stat-value stat-value-orange">{{ unsupportedCount }}</div>
        <div class="stat-label">不支持的文件</div>
      </div>
    </div>

    <div class="folder-view-header">
      <span class="section-title">素材文件夹视图</span>
      <span class="scan-time">最近扫描：{{ lastScanTime || '未扫描' }}</span>
    </div>

    <div class="folder-grid">
      <div class="folder-card" v-for="folder in folders" :key="folder.name">
        <div class="folder-card-header">
          <span class="folder-name">{{ folder.name }}</span>
          <span class="folder-tag" v-if="folder.clips.length < MIN_CLIPS">素材较少</span>
        </div>
        <div class="folder-path">{{ folder.path }}</div>
        <div class="folder-clip-count">{{ folder.clips.length }} 个片段</div>
        <div class="folder-file-list">
          <div
            class="file-item"
            v-for="(clip, idx) in folder.clips.slice(0, 4)"
            :key="idx"
          >
            <span class="file-type-icon">{{ clip.fileType === 'image' ? '🖼' : clip.fileType === 'video' ? '🎬' : '🎵' }}</span>
            <span class="file-name" :title="clip.name">{{ clip.name }}</span>
          </div>
          <div v-if="folder.clips.length > 4" class="file-more">+{{ folder.clips.length - 4 }} 更多</div>
        </div>
        <div class="folder-actions">
          <a class="action-link" @click="handleViewDetail(folder)">查看详情</a>
        </div>
      </div>
    </div>

    <a-modal
      v-model:open="detailModalVisible"
      :title="detailFolder?.name || '文件夹详情'"
      width="720px"
      :footer="null"
    >
      <div v-if="detailFolder" class="detail-modal">
        <div class="detail-info">
          <span class="detail-path">{{ detailFolder.path }}</span>
          <span class="detail-count">{{ detailFolder.clips.length }} 个片段</span>
        </div>
        <div class="detail-clip-list">
          <div class="detail-clip-item" v-for="(clip, idx) in detailFolder.clips" :key="idx">
            <div class="clip-info">
              <div class="clip-name" :title="clip.name">{{ clip.name }}</div>
              <div class="clip-meta">
                <span>{{ clip.sizeText }}</span>
                <span>{{ clip.durationText }}</span>
                <span class="clip-type-tag">{{ clip.fileType === 'video' ? '视频' : clip.fileType === 'image' ? '图片' : '音频' }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </a-modal>

  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import {
  FolderOpenOutlined,
} from '@ant-design/icons-vue';
import { message } from 'ant-design-vue';

const VIDEO_EXTENSIONS = ['.mp4', '.avi', '.mov', '.mkv', '.wmv', '.flv', '.webm', '.m4v', '.mpg', '.mpeg', '.3gp', '.ts'];
const IMAGE_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp', '.svg', '.ico'];
const AUDIO_EXTENSIONS = ['.mp3', '.wav', '.flac', '.aac', '.ogg', '.wma', '.m4a'];
const STORAGE_KEY = 'material_center_state';
const DB_NAME = 'material_center_db';
const DB_STORE = 'files';

const rootPath = ref('');
const selectedFiles = ref([]);
const selecting = ref(false);
const lastScanTime = ref('');

const folders = ref([]);
const unsupportedCount = ref(0);

const detailModalVisible = ref(false);
const detailFolder = ref(null);

const folderInputRef = ref(null);

const folderCount = computed(() => folders.value.length);
const totalClips = computed(() => folders.value.reduce((sum, f) => sum + f.clips.length, 0));

function openDB() {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, 1);
    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      if (!db.objectStoreNames.contains(DB_STORE)) {
        db.createObjectStore(DB_STORE);
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

async function saveFileToDB(key, file) {
  try {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(DB_STORE, 'readwrite');
      const store = tx.objectStore(DB_STORE);
      // Store file with metadata
      const fileData = {
        file: file,
        name: file.name,
        webkitRelativePath: file.webkitRelativePath || key,
        size: file.size,
        type: file.type,
        key: key,
      };
      store.put(fileData, key);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  } catch (e) {
    console.warn('Failed to save file to DB:', e);
  }
}

async function clearDB() {
  try {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(DB_STORE, 'readwrite');
      const store = tx.objectStore(DB_STORE);
      store.clear();
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  } catch (e) {
    console.warn('Failed to clear DB:', e);
  }
}

onMounted(() => {
  loadState();
});

async function loadState() {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      const state = JSON.parse(saved);
      if (state.rootPath) {
        rootPath.value = state.rootPath;
        folders.value = state.folders || [];
        lastScanTime.value = state.lastScanTime || '';
        unsupportedCount.value = state.unsupportedCount || 0;
      } else {
        resetState();
      }
    } else {
      resetState();
    }
  } catch (e) {
    console.warn('Failed to load saved state:', e);
    resetState();
  }
}

function resetState() {
  rootPath.value = '';
  folders.value = [];
  lastScanTime.value = '';
  unsupportedCount.value = 0;
  selectedFiles.value = [];
}

function saveState() {
  try {
    const state = {
      rootPath: rootPath.value,
      folders: folders.value.map(f => ({
        name: f.name,
        path: f.path,
        clips: f.clips.map(c => ({
          name: c.name,
          sizeText: c.sizeText,
          durationText: c.durationText,
          fileType: c.fileType || 'video',
          fileKey: c.fileKey || '',
        })),
      })),
      lastScanTime: lastScanTime.value,
      unsupportedCount: unsupportedCount.value,
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch (e) {
    console.warn('Failed to save state:', e);
  }
}

function triggerFolderSelect() {
  if (folderInputRef.value) {
    folderInputRef.value.click();
  }
}

async function onFolderSelected(event) {
  const files = event.target.files;
  if (!files || files.length === 0) return;

  selecting.value = true;
  try {
    selectedFiles.value = Array.from(files);

    const firstFile = files[0];
    const pathParts = firstFile.webkitRelativePath.split('/');
    const rootFolderName = pathParts[0];
    rootPath.value = rootFolderName;

    message.success(`已选择文件夹：${rootFolderName}，共 ${files.length} 个文件`);

    await processFiles(files);
  } catch (e) {
    message.error('处理文件夹失败：' + e.message);
  } finally {
    selecting.value = false;
    event.target.value = '';
  }
}

async function processFiles(files) {
  const folderMap = new Map();
  unsupportedCount.value = 0;

  await clearDB();

  const HIDDEN_FILES = ['desktop.ini', 'thumbs.db', '.ds_store'];

  for (const file of files) {
    if (HIDDEN_FILES.includes(file.name.toLowerCase())) continue;

    const pathParts = file.webkitRelativePath.split('/');
    if (pathParts.length < 2) continue;

    const folderName = pathParts[1];
    const ext = '.' + file.name.split('.').pop().toLowerCase();

    if (!VIDEO_EXTENSIONS.includes(ext) && !IMAGE_EXTENSIONS.includes(ext) && !AUDIO_EXTENSIONS.includes(ext)) {
      unsupportedCount.value++;
      continue;
    }

    if (!folderMap.has(folderName)) {
      folderMap.set(folderName, {
        name: folderName,
        path: rootPath.value + '\\' + folderName,
        clips: [],
      });
    }

    const sizeText = formatFileSize(file.size);
    let durationText = '--:--';
    const fileKey = `${folderName}/${file.name}`;

    await saveFileToDB(fileKey, file);

    if (VIDEO_EXTENSIONS.includes(ext)) {
      durationText = await getVideoDuration(file);
    } else if (AUDIO_EXTENSIONS.includes(ext)) {
      durationText = await getAudioDuration(file);
    }

    folderMap.get(folderName).clips.push({
      name: file.name,
      sizeText,
      durationText,
      fileType: VIDEO_EXTENSIONS.includes(ext) ? 'video' : IMAGE_EXTENSIONS.includes(ext) ? 'image' : 'audio',
      fileKey: fileKey,
    });
  }

  folders.value = Array.from(folderMap.values());
  lastScanTime.value = formatTime(new Date());
  saveState();
}

function handleViewDetail(folder) {
  detailFolder.value = folder;
  detailModalVisible.value = true;
}

function getVideoDuration(file) {
  return new Promise((resolve) => {
    const timeout = setTimeout(() => {
      resolve('--:--');
      URL.revokeObjectURL(video.src);
    }, 5000);

    const video = document.createElement('video');
    video.preload = 'metadata';
    video.onloadedmetadata = () => {
      clearTimeout(timeout);
      const d = video.duration;
      const mins = Math.floor(d / 60);
      const secs = Math.floor(d % 60);
      resolve(`${mins}:${secs.toString().padStart(2, '0')}`);
      URL.revokeObjectURL(video.src);
    };
    video.onerror = () => {
      clearTimeout(timeout);
      resolve('--:--');
      URL.revokeObjectURL(video.src);
    };
    video.src = URL.createObjectURL(file);
  });
}

function getAudioDuration(file) {
  return new Promise((resolve) => {
    const audio = document.createElement('audio');
    audio.preload = 'metadata';
    audio.onloadedmetadata = () => {
      const d = audio.duration;
      const mins = Math.floor(d / 60);
      const secs = Math.floor(d % 60);
      resolve(`${mins}:${secs.toString().padStart(2, '0')}`);
      URL.revokeObjectURL(audio.src);
    };
    audio.onerror = () => resolve('--:--');
    audio.src = URL.createObjectURL(file);
  });
}

function formatFileSize(bytes) {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  if (bytes < 1024 * 1024 * 1024) return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  return (bytes / (1024 * 1024 * 1024)).toFixed(2) + ' GB';
}

function formatTime(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  const h = String(date.getHours()).padStart(2, '0');
  const min = String(date.getMinutes()).padStart(2, '0');
  return `${y}/${m}/${d} ${h}:${min}`;
}
</script>

<style scoped>
.material-center {
  min-height: 100%;
  padding-top: 20px;
}

.banner-tip {
  background: #e6f4ff;
  border: 1px solid #91caff;
  border-radius: 8px;
  padding: 12px 16px;
  font-size: 13px;
  color: #0958d9;
  margin-bottom: 20px;
  line-height: 1.6;
}

.toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  flex-wrap: wrap;
  gap: 12px;
}

.toolbar-left {
  display: flex;
  align-items: center;
  gap: 12px;
}

.toolbar-label {
  font-size: 14px;
  color: #595959;
}

.toolbar-path {
  font-size: 14px;
  color: #1a1a1a;
  font-weight: 500;
  background: #f5f5f5;
  padding: 4px 12px;
  border-radius: 6px;
}

.toolbar-right {
  display: flex;
  align-items: center;
  gap: 10px;
}

.btn-select-folder {
  border-radius: 8px;
}

.stat-cards {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
  margin-bottom: 24px;
}

.stat-card {
  background: #fff;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
}

.stat-value {
  font-size: 32px;
  font-weight: 700;
  line-height: 1.2;
  margin-bottom: 8px;
}

.stat-value-blue {
  color: #1677ff;
}

.stat-value-green {
  color: #52c41a;
}

.stat-value-orange {
  color: #fa8c16;
}

.stat-label {
  font-size: 13px;
  color: #8c8c8c;
}

.folder-view-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.section-title {
  font-size: 16px;
  font-weight: 600;
  color: #1a1a1a;
}

.scan-time {
  font-size: 13px;
  color: #8c8c8c;
}

.folder-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
}

.folder-card {
  background: #fff;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
}

.folder-card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 4px;
}

.folder-name {
  font-size: 15px;
  font-weight: 600;
  color: #1a1a1a;
}

.folder-tag {
  font-size: 12px;
  color: #fa8c16;
  background: #fff7e6;
  padding: 2px 8px;
  border-radius: 4px;
}

.folder-path {
  font-size: 12px;
  color: #8c8c8c;
  margin-bottom: 4px;
}

.folder-clip-count {
  font-size: 12px;
  color: #8c8c8c;
  margin-bottom: 12px;
}

.folder-file-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 16px;
  max-height: 120px;
  overflow: hidden;
}

.file-item {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: #595959;
}

.file-type-icon {
  font-size: 14px;
  flex-shrink: 0;
}

.file-name {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.file-more {
  font-size: 12px;
  color: #4096ff;
  padding-left: 22px;
}

.folder-actions {
  display: flex;
  gap: 16px;
}

.action-link {
  font-size: 13px;
  color: #4096ff;
  cursor: pointer;
}

.action-link:hover {
  color: #1677ff;
}

.detail-modal {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.detail-info {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 12px;
  border-bottom: 1px solid #f0f0f0;
}

.detail-path {
  font-size: 13px;
  color: #8c8c8c;
}

.detail-count {
  font-size: 13px;
  color: #1a1a1a;
  font-weight: 600;
}

.detail-clip-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
  max-height: 400px;
  overflow-y: auto;
}

.detail-clip-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px;
  border-radius: 8px;
  border: 1px solid #f0f0f0;
}

.clip-type-tag {
  font-size: 11px;
  padding: 1px 6px;
  border-radius: 3px;
  background: #f0f0f0;
  color: #595959;
}

.clip-info {
  flex: 1;
  min-width: 0;
}

.clip-name {
  font-size: 13px;
  color: #1a1a1a;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.clip-meta {
  display: flex;
  gap: 12px;
  font-size: 12px;
  color: #8c8c8c;
  margin-top: 4px;
}
</style>
